import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAllBoardChannels } from '@/hooks/useAllBoardChannels'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ViewListIcon from '@mui/icons-material/ViewList'
import AddIcon from '@mui/icons-material/Add'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'
import { KanbanColumn, Status } from '@/components/tasks/KanbanColumn'
import { SuperTaskCard, SuperTask } from '@/components/tasks/SuperTaskCard'
import { SuperTaskFormDialog } from '@/components/tasks/SuperTaskFormDialog'

const COLLAPSED_COLUMNS_KEY = 'okonomi-kanban-collapsed-columns'
const EXPANDED_EMPTY_KEY = 'okonomi-kanban-expanded-empty-columns'
const INITIALIZED_KEY = 'okonomi-kanban-initialized'
const DEFAULT_EXPANDED_COLUMNS = ['pending', 'in_progress', 'completed', 'accepted']
const FILTERS_KEY = 'okonomi-all-tasks-filters'
const VIEW_MODE_KEY = 'okonomi-all-tasks-view-mode'

function loadFromLocalStorage(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key)
    if (stored) {
      return new Set(JSON.parse(stored))
    }
  } catch {
    // ignore parse errors
  }
  return new Set()
}

function saveToLocalStorage(key: string, value: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...value]))
}

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface BoardRef {
  id: number
  name: string
  slug: string
  color: string | null
  project_id: number
}

interface ProjectRef {
  id: number
  name: string
  slug: string
  color: string | null
}

interface BoardWithAgents {
  id: number
  name: string
  slug: string
  color: string | null
  project: ProjectRef
  agents: AgentRef[]
}

interface Filters {
  project_id: number | null
  board_id: number | null
  status: string | null
  assignee_id: number | null
  q: string | null
}

interface Props {
  tasks: SuperTask[]
  projects: ProjectRef[]
  boards: BoardRef[]
  agents: AgentRef[]
  statuses: Status[]
  filters: Filters
  accessible_boards_with_agents: BoardWithAgents[]
}

export default function AllTasks({ tasks, projects, boards, agents, statuses, filters, accessible_boards_with_agents }: Props) {
  const [showNewTask, setShowNewTask] = useState(false)
  const [searchValue, setSearchValue] = useState(filters.q || '')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_KEY)
      if (stored === 'kanban' || stored === 'list') return stored
    } catch { /* ignore */ }
    return isMobile ? 'list' : 'kanban'
  })

  // Collapse state: manually collapsed columns (persisted)
  const [manuallyCollapsed, setManuallyCollapsed] = useState<Set<string>>(() => loadFromLocalStorage(COLLAPSED_COLUMNS_KEY))
  // Track columns the user has explicitly expanded even though they're empty
  // On first visit, default columns (pending, in_progress, completed, accepted) start expanded
  const [expandedEmpty, setExpandedEmpty] = useState<Set<string>>(() => {
    if (!localStorage.getItem(INITIALIZED_KEY)) {
      const defaults = new Set(DEFAULT_EXPANDED_COLUMNS)
      saveToLocalStorage(EXPANDED_EMPTY_KEY, defaults)
      localStorage.setItem(INITIALIZED_KEY, '1')
      return defaults
    }
    return loadFromLocalStorage(EXPANDED_EMPTY_KEY)
  })

  // Persist view mode to localStorage
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode) } catch { /* ignore */ }
  }, [viewMode])

  // Restore saved filters on initial load when no URL filters are active
  const hasRestoredFilters = useRef(false)
  useEffect(() => {
    if (hasRestoredFilters.current) return
    hasRestoredFilters.current = true

    const hasUrlFilters = filters.project_id || filters.board_id || filters.status || filters.assignee_id || filters.q
    if (hasUrlFilters) return

    try {
      const stored = localStorage.getItem(FILTERS_KEY)
      if (stored) {
        const savedFilters = JSON.parse(stored)
        if (Object.values(savedFilters).some(v => v !== null && v !== undefined && v !== '')) {
          router.get('/tasks/all', savedFilters, { preserveState: true })
        }
      }
    } catch { /* ignore */ }
  }, [])

  // Subscribe to all board channels for live task updates
  const boardIds = useMemo(() => boards.map(b => b.id), [boards])
  useAllBoardChannels(boardIds)

  const filteredBoards = useMemo(() => {
    if (!filters.project_id) return boards
    return boards.filter(b => b.project_id === filters.project_id)
  }, [boards, filters.project_id])

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, SuperTask[]> = {}
    statuses.forEach((s) => {
      grouped[s.key] = []
    })
    tasks.forEach((t) => {
      if (grouped[t.status]) {
        grouped[t.status].push(t)
      }
    })
    return grouped
  }, [statuses, tasks])

  // Determine which columns are collapsed: manually collapsed OR (empty and not explicitly expanded)
  const collapsedColumns = useMemo(() => {
    const collapsed = new Set<string>()
    statuses.forEach((s) => {
      const isEmpty = (tasksByStatus[s.key] || []).length === 0
      const isManuallyCollapsed = manuallyCollapsed.has(s.key)
      const isExplicitlyExpanded = expandedEmpty.has(s.key)

      if (isManuallyCollapsed || (isEmpty && !isExplicitlyExpanded)) {
        collapsed.add(s.key)
      }
    })
    return collapsed
  }, [statuses, tasksByStatus, manuallyCollapsed, expandedEmpty])

  // Clean up expandedEmpty entries for columns that now have tasks
  useEffect(() => {
    let changed = false
    const updated = new Set(expandedEmpty)
    for (const key of updated) {
      if ((tasksByStatus[key] || []).length > 0) {
        updated.delete(key)
        changed = true
      }
    }
    if (changed) {
      setExpandedEmpty(updated)
      saveToLocalStorage(EXPANDED_EMPTY_KEY, updated)
    }
  }, [tasksByStatus])

  const toggleCollapse = useCallback((statusKey: string) => {
    const isEmpty = (tasksByStatus[statusKey] || []).length === 0
    const isCurrentlyCollapsed = collapsedColumns.has(statusKey)

    if (isCurrentlyCollapsed) {
      // Expanding
      // Remove from manually collapsed
      const newManual = new Set(manuallyCollapsed)
      newManual.delete(statusKey)
      setManuallyCollapsed(newManual)
      saveToLocalStorage(COLLAPSED_COLUMNS_KEY, newManual)

      // If it's empty, add to expanded-empty so auto-collapse doesn't re-collapse it
      if (isEmpty) {
        const newExpanded = new Set(expandedEmpty)
        newExpanded.add(statusKey)
        setExpandedEmpty(newExpanded)
        saveToLocalStorage(EXPANDED_EMPTY_KEY, newExpanded)
      }
    } else {
      // Collapsing
      const newManual = new Set(manuallyCollapsed)
      newManual.add(statusKey)
      setManuallyCollapsed(newManual)
      saveToLocalStorage(COLLAPSED_COLUMNS_KEY, newManual)

      // Remove from expanded-empty if present
      if (expandedEmpty.has(statusKey)) {
        const newExpanded = new Set(expandedEmpty)
        newExpanded.delete(statusKey)
        setExpandedEmpty(newExpanded)
        saveToLocalStorage(EXPANDED_EMPTY_KEY, newExpanded)
      }
    }
  }, [tasksByStatus, collapsedColumns, manuallyCollapsed, expandedEmpty])

  function applyFilter(key: string, value: any) {
    const newFilters: Record<string, any> = { ...filters, [key]: value || undefined }
    // Reset board filter when project changes
    if (key === 'project_id') {
      newFilters.board_id = undefined
    }
    // Remove empty values
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === null || newFilters[k] === undefined || newFilters[k] === '') {
        delete newFilters[k]
      }
    })
    // Persist filters to localStorage
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(newFilters)) } catch { /* ignore */ }
    router.get('/tasks/all', newFilters, { preserveState: true })
  }

  function handleSearch() {
    applyFilter('q', searchValue || undefined)
  }

  function handleTaskClick(task: SuperTask) {
    router.visit(`/tasks/boards/${task.board.slug}/tasks/${task.id}`)
  }

  const hasActiveFilters = filters.project_id || filters.board_id || filters.status || filters.assignee_id || filters.q

  return (
    <AppLayout
      maxWidth={false}
    >
      {/* Header: title + filters + actions in one row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, mr: 1 }}>
          Tasks
        </Typography>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.project_id || ''}
            label="Project"
            onChange={e => applyFilter('project_id', e.target.value || null)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: p.color || '#6B7280', flexShrink: 0 }} />
                  {p.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Board</InputLabel>
          <Select
            value={filters.board_id || ''}
            label="Board"
            onChange={e => applyFilter('board_id', e.target.value || null)}
          >
            <MenuItem value="">All Boards</MenuItem>
            {filteredBoards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: b.color || '#6B7280', flexShrink: 0 }} />
                  {b.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => applyFilter('status', e.target.value || null)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {statuses.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                  {s.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Assignee</InputLabel>
          <Select
            value={filters.assignee_id || ''}
            label="Assignee"
            onChange={e => applyFilter('assignee_id', e.target.value || null)}
          >
            <MenuItem value="">All Agents</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search..."
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 160 }}
        />

        {hasActiveFilters && (
          <Button
            size="small"
            onClick={() => {
              try { localStorage.removeItem(FILTERS_KEY) } catch { /* ignore */ }
              router.get('/tasks/all', {}, { preserveState: true })
            }}
          >
            Clear
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="kanban" sx={{ minHeight: 36, px: 1.5 }}>
            <ViewColumnIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="list" sx={{ minHeight: 36, px: 1.5 }}>
            <ViewListIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowNewTask(true)}>
          New task
        </Button>
      </Box>

      {viewMode === 'kanban' ? (
        <Box
          sx={{
            display: 'flex',
            height: 'calc(100vh - 200px)',
            border: '1px solid',
            borderColor: 'divider',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {statuses.map((status) => (
            <KanbanColumn
              key={status.key}
              status={status}
              tasks={tasksByStatus[status.key] || []}
              onTaskClick={(task) => handleTaskClick(task as SuperTask)}
              showBoardProject
              seamless
              isCollapsed={collapsedColumns.has(status.key)}
              onToggleCollapse={() => toggleCollapse(status.key)}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {statuses.map((status) => {
            const statusTasks = tasksByStatus[status.key] || []
            if (statusTasks.length === 0) return null
            return (
              <Box key={status.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: status.color, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600}>{status.name}</Typography>
                  <Chip label={statusTasks.length} size="small" variant="outlined" sx={{ '& .MuiChip-label': { px: '6px', lineHeight: 1 } }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {statusTasks.map((task) => (
                    <SuperTaskCard key={task.id} task={task} onClick={() => handleTaskClick(task)} />
                  ))}
                </Box>
              </Box>
            )
          })}
          {tasks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
              No tasks found.
            </Typography>
          )}
        </Box>
      )}

      <SuperTaskFormDialog
        open={showNewTask}
        onClose={() => setShowNewTask(false)}
        boards={accessible_boards_with_agents}
      />
    </AppLayout>
  )
}
